import { Angebot, useAngebotList } from 'api/angebot';
import { Auftrag, useAuftragList } from 'api/auftrag';
import { Lieferung, useLieferungList } from 'api/lieferung';
import { Rechnung, useRechnungList } from 'api/rechnung';
import * as React from 'react';
import usePersistentState from 'hooks/usePersistentState';
import { createRecordUrl, formatDecimal, formatMoney } from 'utils/formatter';
import moment from 'moment-timezone';
import { OpenLink } from 'formComponents/OpenLink/OpenLink';
import { formStyles } from 'styles/formStyles';
import { useSelector } from 'react-redux';
import { betreuerByNrSelector } from 'store/lookups/selector';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import classnames from 'classnames';
import { viewStyles } from 'styles/viewStyles';
import { LoadingIndicator } from 'layout/LoadingIndicator/LoadingIndicator';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import AutoSizer from 'react-virtualized-auto-sizer';
import { List } from '@fluentui/react/lib/List';
import { Bestellung, useBestellungList } from 'api/bestellung';
import { AngebotPositionFilter, useAngebotPositionList } from 'api/angebotPosition';
import { ServicePositionFilter, useServicePositionList } from 'api/servicePosition';
import { AuftragPositionFilter, useAuftragPositionList } from 'api/auftragPosition';
import { BestellungPositionFilter, useBestellungPositionList } from 'api/bestellungPosition';
import { LieferungPositionFilter, useLieferungPositionList } from 'api/lieferungPosition';
import { RechnungPositionFilter, useRechnungPositionList } from 'api/rechnungPosition';
import {
    AngebotStatusLabel,
    ServiceStatusLabel,
    AuftragStatusLabel,
    BestellungStatusLabel,
    LieferungStatusLabel,
    RechnungStatusLabel,
} from 'utils/labels';
import { Service, useServiceList } from 'api/service';
import { uniq } from 'lodash';
import { ValueType } from 'react-select';
import { SelectOption } from 'formComponents/Select/types';
import { MultiSelect } from 'formComponents/Select/MultiSelect';

const styles = mergeStyleSets({
    container: {
        height: 580,
        display: 'grid',
        gridTemplateRows: 'max-content max-content max-content 1fr',
    },
    searchBarLine1: {
        display: 'grid',
        gridTemplateColumns: '120px 120px 120px 120px 120px 120px 1fr',
        gridColumnGap: 10,
        marginTop: 10,
    },
    searchBarLine2: {
        display: 'grid',
        gridTemplateColumns: '120px 120px 120px 120px 120px',
        gridColumnGap: 10,
        marginTop: 10,
    },
    listItem: {
        display: 'grid',
        marginTop: 5,
        gridTemplateColumns: '80px 80px 1fr 90px 90px 100px 90px 30px 110px 110px 110px 110px 110px 1fr',
    },
    listCell: {
        position: 'relative',
    },
    listCellRight: {
        position: 'relative',
        textAlign: 'right',
        paddingRight: 10,
    },
    listHeader: {
        fontWeight: 700,
    },
    text: {
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
    },
});

interface ArtikelHistoryItem {
    type: 'angebot' | 'service' | 'auftrag' | 'bestellung' | 'lieferung' | 'rechnung';
    datum: string;
    record: Angebot | Service | Auftrag | Bestellung | Lieferung | Rechnung;
    pos: number;
    lArt: string;
    menge: number;
    offen: number | null;
    ekPreis: number;
    ePreis: number | null;
    netto: number;

    kName: string;
    label: string;
    recordUrl: string;
    statusLabel: string;
    text: string;
    auNr: string | null;
}

interface ArtikelDetailHistorieProps {
    artikelGruppeNr: number;
    artikelNr: string;
}

export const ArtikelDetailHistorie: React.FC<ArtikelDetailHistorieProps> = ({ artikelGruppeNr, artikelNr }) => {
    const [showAngebote, setShowAngebote] = usePersistentState<boolean>(true, 'ArtikelHistoryView.showAngebote');
    const [showServices, setShowServices] = usePersistentState<boolean>(true, 'ArtikelHistoryView.showServices');
    const [showAuftraege, setShowAuftraege] = usePersistentState<boolean>(true, 'ArtikelHistoryView.showAuftraege');
    const [showBestellungen, setShowBestellungen] = usePersistentState<boolean>(
        true,
        'ArtikelHistoryView.showBestellungen',
    );
    const [showLieferungen, setShowLieferungen] = usePersistentState<boolean>(
        true,
        'ArtikelHistoryView.showLieferungen',
    );
    const [showRechnungen, setShowRechnungen] = usePersistentState<boolean>(true, 'ArtikelHistoryView.showRechnungen');
    const [showLArtD, setShowLArtD] = usePersistentState<boolean>(true, 'ArtikelHistoryView.showLArtD');
    const [showLArtL, setShowLArtL] = usePersistentState<boolean>(true, 'ArtikelHistoryView.showLArtL');
    const [showLArtR, setShowLArtR] = usePersistentState<boolean>(true, 'ArtikelHistoryView.showLArtR');
    const [showLArtX, setShowLArtX] = usePersistentState<boolean>(true, 'ArtikelHistoryView.showLArtX');
    const [showLArtEmpty, setShowLArtEmpty] = usePersistentState<boolean>(true, 'ArtikelHistoryView.showLArtEmpty');
    const betreuerByNr = useSelector(betreuerByNrSelector);

    const angebotList = useAngebotList({ artikelGruppeNr, artikelNr, withKunde: true }, { enabled: showAngebote });
    const angebotPositionFilter = React.useMemo<AngebotPositionFilter>(
        () => ({ aNrList: angebotList.data?.map((a) => a.nr) || [], artikelGruppeNr, artikelNr }),
        [angebotList, artikelGruppeNr, artikelNr],
    );
    const angebotPositionList = useAngebotPositionList(angebotPositionFilter, { enabled: showAngebote });

    const serviceList = useServiceList({ artikelGruppeNr, artikelNr, withKunde: true }, { enabled: showServices });
    const servicePositionFilter = React.useMemo<ServicePositionFilter>(
        () => ({ sNrList: serviceList.data?.map((a) => a.nr) || [], artikelGruppeNr, artikelNr }),
        [artikelGruppeNr, artikelNr, serviceList.data],
    );
    const servicePositionList = useServicePositionList(servicePositionFilter, { enabled: showServices });

    const auftragList = useAuftragList({ artikelGruppeNr, artikelNr, withKunde: true }, { enabled: showAuftraege });
    const auftragPositionFilter = React.useMemo<AuftragPositionFilter>(
        () => ({ auNrList: auftragList.data?.map((au) => au.nr) || [], artikelGruppeNr, artikelNr }),
        [auftragList, artikelGruppeNr, artikelNr],
    );
    const auftragPositionList = useAuftragPositionList(auftragPositionFilter, { enabled: showAuftraege });

    const bestellungList = useBestellungList(
        { artikelGruppeNr, artikelNr, withKunde: true },
        { enabled: showBestellungen },
    );
    const bestellungPositionFilter = React.useMemo<BestellungPositionFilter>(
        () => ({ bNrList: bestellungList.data?.map((b) => b.nr) || [], artikelGruppeNr, artikelNr }),
        [bestellungList, artikelGruppeNr, artikelNr],
    );
    const bestellungPositionList = useBestellungPositionList(bestellungPositionFilter, { enabled: showBestellungen });

    const lieferungList = useLieferungList(
        { artikelGruppeNr, artikelNr, withKunde: true },
        { enabled: showLieferungen },
    );
    const lieferungPositionFilter = React.useMemo<LieferungPositionFilter>(
        () => ({ lNrList: lieferungList.data?.map((l) => l.nr) || [], artikelGruppeNr, artikelNr }),
        [lieferungList, artikelGruppeNr, artikelNr],
    );
    const lieferungPositionList = useLieferungPositionList(lieferungPositionFilter, { enabled: showLieferungen });

    const rechnungList = useRechnungList({ artikelGruppeNr, artikelNr, withKunde: true }, { enabled: showRechnungen });
    const rechnungPositionFilter = React.useMemo<RechnungPositionFilter>(
        () => ({ rNrList: rechnungList.data?.map((r) => r.nr) || [], artikelGruppeNr, artikelNr }),
        [rechnungList, artikelGruppeNr, artikelNr],
    );
    const rechnungPositionList = useRechnungPositionList(rechnungPositionFilter, { enabled: showRechnungen });

    const lArtFilter = React.useCallback(
        (lArt: string) => {
            if (showLArtD && lArt === 'D') {
                return true;
            }
            if (showLArtL && lArt === 'L') {
                return true;
            }
            if (showLArtR && lArt === 'R') {
                return true;
            }
            if (showLArtX && lArt === 'X') {
                return true;
            }
            if (showLArtEmpty && lArt.trim() === '') {
                return true;
            }
            return false;
        },
        [showLArtD, showLArtL, showLArtR, showLArtX, showLArtEmpty],
    );

    const historyItems = React.useMemo<ArtikelHistoryItem[]>(() => {
        const items: ArtikelHistoryItem[] = [];

        if (showAngebote) {
            (angebotList.data || []).forEach((angebot) => {
                (angebotPositionList.data || [])
                    .filter((ap) => ap.aNr === angebot.nr && lArtFilter(''))
                    .forEach((angebotPosition) => {
                        items.push({
                            type: 'angebot',
                            datum: angebot.datum,
                            record: angebot,
                            pos: angebotPosition.pos,
                            lArt: '',
                            menge: angebotPosition.posMenge,
                            offen: null,
                            ekPreis: angebotPosition.posEkPreis,
                            ePreis: angebotPosition.posEPreis,
                            netto: angebotPosition.posNetto,

                            kName: angebot.kunde?.name1 || '',
                            label: 'Angebot',
                            recordUrl: createRecordUrl('angebote', angebot.kNr, angebot.nr),
                            statusLabel: AngebotStatusLabel[angebot.status] || angebot.status.toString(),
                            text: angebot.aktText,
                            auNr: angebot.auNr,
                        });
                    });
            });
        }

        if (showServices) {
            (serviceList.data || []).forEach((service) => {
                (servicePositionList.data || [])
                    .filter((sp) => sp.sNr === service.nr && lArtFilter(''))
                    .forEach((servicePosition) => {
                        items.push({
                            type: 'service',
                            datum: service.datum,
                            record: service,
                            pos: servicePosition.pos,
                            lArt: '',
                            menge: servicePosition.posMenge,
                            offen: null,
                            ekPreis: servicePosition.posEkPreis,
                            ePreis: servicePosition.posEPreis,
                            netto: servicePosition.posNetto,

                            kName: service.kunde?.name1 || '',
                            label: 'Service',
                            recordUrl: createRecordUrl('services', service.kNr, service.nr),
                            statusLabel: ServiceStatusLabel[service.status] || service.status.toString(),
                            text: service.aktText,
                            auNr: service.auNr,
                        });
                    });
            });
        }

        if (showAuftraege) {
            (auftragList.data || []).forEach((auftrag) => {
                (auftragPositionList.data || [])
                    .filter((ap) => ap.auNr === auftrag.nr && lArtFilter(ap.lArt))
                    .forEach((auftragPosition) => {
                        items.push({
                            type: 'auftrag',
                            datum: auftrag.datum,
                            record: auftrag,
                            pos: auftragPosition.pos,
                            lArt: auftragPosition.lArt,
                            menge: auftragPosition.posMenge,
                            offen: auftragPosition.offen,
                            ekPreis: auftragPosition.posEkPreis,
                            ePreis: auftragPosition.posEPreis,
                            netto: auftragPosition.posNetto,

                            kName: auftrag.kunde?.name1 || '',
                            label: 'Auftrag',
                            recordUrl: createRecordUrl('auftraege', auftrag.kNr, auftrag.nr),
                            statusLabel: AuftragStatusLabel[auftrag.status] || auftrag.status.toString(),
                            text: auftrag.aktText,
                            auNr: null,
                        });
                    });
            });
        }

        if (showBestellungen) {
            (bestellungList.data || []).forEach((bestellung) => {
                (bestellungPositionList.data || [])
                    .filter((bp) => bp.bNr === bestellung.nr && lArtFilter(bp.lArt))
                    .forEach((bestellungPosition) => {
                        items.push({
                            type: 'bestellung',
                            datum: bestellung.datum,
                            record: bestellung,
                            pos: bestellungPosition.pos,
                            lArt: bestellungPosition.lArt,
                            menge: bestellungPosition.posMenge,
                            offen: bestellungPosition.offen,
                            ekPreis: bestellungPosition.posEPreis,
                            ePreis: null, // bestellungPosition.posEPreis,
                            netto: bestellungPosition.posNetto,

                            kName: bestellung.kunde?.name1 || '',
                            label: 'Bestellung',
                            recordUrl: createRecordUrl('bestellungen', bestellung.kNr, bestellung.nr),
                            statusLabel: BestellungStatusLabel[bestellung.status] || bestellung.status.toString(),
                            text: bestellung.aktText,
                            auNr: bestellung.auNr,
                        });
                    });
            });
        }

        if (showLieferungen) {
            (lieferungList.data || []).forEach((lieferung) => {
                (lieferungPositionList.data || [])
                    .filter((lp) => lp.lNr === lieferung.nr && lArtFilter(lp.lArt))
                    .forEach((lieferungPosition) => {
                        items.push({
                            type: 'lieferung',
                            datum: lieferung.datum,
                            record: lieferung,
                            pos: lieferungPosition.pos,
                            lArt: lieferungPosition.lArt,
                            menge: lieferungPosition.posMenge,
                            offen: lieferungPosition.offen,
                            ekPreis: lieferungPosition.posEkPreis,
                            ePreis: lieferungPosition.posEPreis,
                            netto: lieferungPosition.posNetto,

                            kName: lieferung.kunde?.name1 || '',
                            label: 'Lieferung',
                            recordUrl: createRecordUrl('lieferungen', lieferung.kNr, lieferung.nr),
                            statusLabel: LieferungStatusLabel[lieferung.status] || lieferung.status.toString(),
                            text: lieferung.aktText,
                            auNr: lieferung.auNr,
                        });
                    });
            });
        }

        if (showRechnungen) {
            (rechnungList.data || []).forEach((rechnung) => {
                (rechnungPositionList.data || [])
                    .filter((rp) => rp.rNr === rechnung.nr && lArtFilter(''))
                    .forEach((rechnungPosition) => {
                        items.push({
                            type: 'rechnung',
                            datum: rechnung.datum,
                            record: rechnung,
                            pos: rechnungPosition.pos,
                            lArt: '',
                            menge: rechnungPosition.posMenge,
                            offen: null,
                            ekPreis: rechnungPosition.posEkPreis,
                            ePreis: rechnungPosition.posEPreis,
                            netto: rechnungPosition.posNetto,

                            kName: rechnung.kunde?.name1 || '',
                            label: 'Rechnung',
                            recordUrl: createRecordUrl('rechnungen', rechnung.kNr, rechnung.nr),
                            statusLabel: RechnungStatusLabel[rechnung.status] || rechnung.status.toString(),
                            text: rechnung.aktText,
                            auNr: rechnung.auNr,
                        });
                    });
            });
        }

        return items.sort((a, b) => b.datum.localeCompare(a.datum));
    }, [
        showAngebote,
        showServices,
        showAuftraege,
        showBestellungen,
        showLieferungen,
        showRechnungen,
        angebotList.data,
        angebotPositionList.data,
        lArtFilter,
        serviceList.data,
        servicePositionList.data,
        auftragList.data,
        auftragPositionList.data,
        bestellungList.data,
        bestellungPositionList.data,
        lieferungList.data,
        lieferungPositionList.data,
        rechnungList.data,
        rechnungPositionList.data,
    ]);

    const [statusLabels, setStatusLabels] = React.useState<string[]>([]);
    const [selectedStatusLabels, setSelectedStatusLabels] = React.useState<string[]>([]);

    React.useEffect(() => {
        const newStatusLabels = uniq(historyItems.map((item) => item.statusLabel));

        setStatusLabels(newStatusLabels);
        setSelectedStatusLabels(newStatusLabels);
    }, [historyItems]);

    const filteredHistoryItems = React.useMemo(() => {
        return historyItems.filter((item) => selectedStatusLabels.includes(item.statusLabel));
    }, [historyItems, selectedStatusLabels]);

    const listRef = React.useRef<List<ArtikelHistoryItem>>(null);

    const onRenderCell = React.useCallback(
        (item: ArtikelHistoryItem | undefined, index: number | undefined): JSX.Element => {
            if (!item) {
                return <div />;
            }

            const kz = item.record.anlageUser
                ? betreuerByNr[item.record.anlageUser]?.kz || item.record.anlageUser.toString()
                : '';
            const recordNr = item.record.nr;
            const { pos, lArt } = item;
            const menge = formatDecimal(item.menge, 3);
            const offen = item.offen == null ? '' : formatDecimal(item.offen, 3);
            const ekPreis = formatMoney(item.ekPreis, 3);
            const ePreis = item.ePreis == null ? '' : formatMoney(item.ePreis, 3);
            const netto = formatMoney(item.netto);

            return (
                <div
                    className={styles.listItem}
                    key={item.datum + '|' + item.type + '|' + recordNr + '|' + pos + '|' + index}
                >
                    <div>{moment(item.datum).format('DD.MM.YYYY')}</div>
                    <div>{kz}</div>
                    <div>{item.kName}</div>
                    <div>{item.label}</div>
                    <div className={styles.listCell}>
                        {recordNr && <OpenLink link={item.recordUrl} label={recordNr} />}
                    </div>
                    <div>{item.statusLabel}</div>
                    {item.auNr ? (
                        <div className={styles.listCell}>
                            <OpenLink
                                link={createRecordUrl('auftraege', item.record.kNr, item.auNr)}
                                label={item.auNr}
                            />
                        </div>
                    ) : (
                        <div />
                    )}
                    <div>{lArt}</div>
                    <div className={styles.listCellRight}>{menge}</div>
                    <div className={styles.listCellRight}>{offen}</div>
                    <div className={styles.listCellRight}>{ekPreis}</div>
                    <div className={styles.listCellRight}>{ePreis}</div>
                    <div className={styles.listCellRight}>{netto}</div>
                    <div className={styles.text} title={item.text}>
                        {item.text}
                    </div>
                </div>
            );
        },
        [betreuerByNr],
    );

    return (
        <div className={styles.container}>
            {angebotList.isLoading ||
            angebotList.isFetching ||
            auftragList.isLoading ||
            auftragList.isFetching ||
            bestellungList.isLoading ||
            bestellungList.isFetching ||
            lieferungList.isLoading ||
            lieferungList.isFetching ||
            rechnungList.isLoading ||
            rechnungList.isFetching ? (
                <LoadingIndicator />
            ) : (
                <>
                    <div className={styles.searchBarLine1}>
                        <Checkbox
                            className={formStyles.checkboxInsideForm}
                            label="Angebote"
                            checked={showAngebote}
                            onChange={(event, checked) => {
                                setShowAngebote(!!checked);
                            }}
                        />
                        <Checkbox
                            className={formStyles.checkboxInsideForm}
                            label="Services"
                            checked={showServices}
                            onChange={(event, checked) => {
                                setShowServices(!!checked);
                            }}
                        />
                        <Checkbox
                            className={formStyles.checkboxInsideForm}
                            label="Aufträge"
                            checked={showAuftraege}
                            onChange={(event, checked) => {
                                setShowAuftraege(!!checked);
                            }}
                        />
                        <Checkbox
                            className={formStyles.checkboxInsideForm}
                            label="Bestellungen"
                            checked={showBestellungen}
                            onChange={(event, checked) => {
                                setShowBestellungen(!!checked);
                            }}
                        />
                        <Checkbox
                            className={formStyles.checkboxInsideForm}
                            label="Lieferungen"
                            checked={showLieferungen}
                            onChange={(event, checked) => {
                                setShowLieferungen(!!checked);
                            }}
                        />
                        <Checkbox
                            className={formStyles.checkboxInsideForm}
                            label="Rechnungen"
                            checked={showRechnungen}
                            onChange={(event, checked) => {
                                setShowRechnungen(!!checked);
                            }}
                        />

                        <MultiSelect
                            name="Status"
                            isClearable
                            value={selectedStatusLabels.map((label) => ({ value: label, label }))}
                            options={statusLabels.map((label) => ({ value: label, label }))}
                            menuPlacement="bottom"
                            onChange={(options: ValueType<SelectOption, true>): void => {
                                setSelectedStatusLabels((options ?? []).map((option) => option.value));
                            }}
                            placeholder="Status"
                        />
                    </div>
                    <div className={styles.searchBarLine2}>
                        <Checkbox
                            className={formStyles.checkboxInsideForm}
                            label="LArt-D"
                            checked={showLArtD}
                            onChange={(event, checked) => {
                                setShowLArtD(!!checked);
                            }}
                        />
                        <Checkbox
                            className={formStyles.checkboxInsideForm}
                            label="LArt-L"
                            checked={showLArtL}
                            onChange={(event, checked) => {
                                setShowLArtL(!!checked);
                            }}
                        />
                        <Checkbox
                            className={formStyles.checkboxInsideForm}
                            label="LArt-R"
                            checked={showLArtR}
                            onChange={(event, checked) => {
                                setShowLArtR(!!checked);
                            }}
                        />
                        <Checkbox
                            className={formStyles.checkboxInsideForm}
                            label="LArt-X"
                            checked={showLArtX}
                            onChange={(event, checked) => {
                                setShowLArtX(!!checked);
                            }}
                        />
                        <Checkbox
                            className={formStyles.checkboxInsideForm}
                            label="LArt-Leer"
                            checked={showLArtEmpty}
                            onChange={(event, checked) => {
                                setShowLArtEmpty(!!checked);
                            }}
                        />
                        <div />
                    </div>
                    <div className={viewStyles.listContainer}>
                        <div className={classnames(styles.listItem, styles.listHeader)}>
                            <div>Datum</div>
                            <div>Benutzer</div>
                            <div>Kunde</div>
                            <div>Nummer</div>
                            <div>Art</div>
                            <div>Status</div>
                            <div>Auftrag</div>
                            <div>LArt</div>
                            <div className={styles.listCellRight}>Menge</div>
                            <div className={styles.listCellRight}>Offen</div>
                            <div className={styles.listCellRight}>EK-Preis</div>
                            <div className={styles.listCellRight}>VK-Preis</div>
                            <div className={styles.listCellRight}>Summe</div>
                            <div>Text</div>
                            <div />
                        </div>
                    </div>
                    <div className={viewStyles.listContainer}>
                        {historyItems.length === 0 ? (
                            <div
                                className={viewStyles.noData}
                                onScroll={() => {
                                    // update doesn't trigger automatically inside a dialog (probably a bug?)
                                    listRef.current?.forceUpdate();
                                }}
                            >
                                Keine Einträge.
                            </div>
                        ) : (
                            <AutoSizer>
                                {({ height, width }) => (
                                    <div style={{ width, height, overflowX: 'hidden', overflowY: 'scroll' }}>
                                        <List ref={listRef} items={filteredHistoryItems} onRenderCell={onRenderCell} />
                                    </div>
                                )}
                            </AutoSizer>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};
